import React, { FormEvent, useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Button, Captcha, CaptchaHandle, useToast } from '@app/core';
import { CodeInput, PhoneNumberInput } from '@app/form';
import { Formik } from 'formik';
import {
  ApiException,
  useMe,
  useSendPhoneValidationCode,
  useUpdateParticipant,
  useVerifyPhoneValidationCode,
} from '@app/api';
import Phone from '@app/illu/Large/PhoneCall.svg';
import { parsePhoneNumber } from 'libphonenumber-js';

const useStyles = createUseStyles((theme: Theme) => ({
  text: {
    ...theme.fonts.body,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 4,
  },
  phoneDisplay: {
    ...theme.fonts.body,
    color: theme.colors.inkPrimary,
    fontWeight: 'bold',
    marginBottom: theme.marginBase * 2,
    textAlign: 'center',
  },
  changePhone: {
    ...theme.fonts.caption,
    textAlign: 'center',
    marginBottom: theme.marginBase * 4,
  },
  changePhoneLink: {
    color: theme.colors.blueAccessible,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  error: {
    ...theme.fonts.body,
    textAlign: 'center',
    color: theme.colors.redAccessible,
    marginTop: theme.marginBase * 2,
  },
  illustration: {
    margin: 'auto',
    width: '100%',
    maxWidth: 116,
  },
  illustrationContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.marginBase,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.marginBase * 4,
  },
  updateButtonGroup: {
    display: 'flex',
    gap: theme.marginBase,
    width: '100%',
    alignItems: 'right',
    justifyContent: 'right',
    marginBottom: theme.marginBase * 4,
  },
  codeSection: {
    marginTop: theme.marginBase * 4,
  },
  resend: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase * 4,
    textAlign: 'center',
  },
  resendLink: {
    color: theme.colors.blueAccessible,
    fontWeight: 'bold',
  },
}));

interface Props {
  phone?: string;
  text?: React.ReactNode | string;
  participantId?: string;
  onTitleChange?: (newTitle: string) => void;
  onSuccess?: () => void;
}

export const VerifyPhoneViaCodeContent = ({
  phone,
  text,
  participantId,
  onTitleChange,
  onSuccess,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation('common');
  const toast = useToast();

  const { mutateAsync: update, isLoading: updating } =
    useUpdateParticipant(participantId);
  const { mutateAsync: sendValidationCode, isLoading: sending } =
    useSendPhoneValidationCode(participantId);
  const { mutateAsync: verifyValidationCode } =
    useVerifyPhoneValidationCode(participantId);
  const { data: me } = useMe();

  const [codeSent, setCodeSent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [notDelivered, setNotDelivered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const codeRef = useRef('');
  const captchaRef = useRef<CaptchaHandle>(null);
  const [solvingCaptcha, setSolvingCaptcha] = useState(false);

  const sendCode = useCallback(async () => {
    try {
      setError(null);

      setSolvingCaptcha(true);
      const altcha = await captchaRef.current?.getPayload().finally(() => {
        setSolvingCaptcha(false);
      });
      await sendValidationCode({ altcha: altcha || '' });

      setCodeSent(true);
      toast.success(t('login.phoneVerificationValidationCode.sent'));
    } catch (e) {
      if (
        e instanceof ApiException &&
        e.response?.data.code === 'SMS_NOT_DELIVERED'
      ) {
        onTitleChange?.(
          t('login.phoneVerificationValidationCode.notDeliveredTitle'),
        );
        setNotDelivered(true);
      }
      toast.exception(e);
    }
  }, [sendValidationCode, toast, t, onTitleChange, setNotDelivered]);

  const resend = useCallback(async () => {
    try {
      setTouched(false);
      setError(null);
      formRef.current?.reset();
      await sendCode();
    } catch (e) {
      toast.exception(e);
    }
  }, [sendCode, toast]);

  const onLetterChanged = useCallback(
    (letters: string) => {
      setTouched(true);
      setError(null);
      codeRef.current = letters;
      if (letters.length === 6) {
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }
        });
      }
    },
    [codeRef, formRef],
  );

  const updatePhone = useCallback(
    async (phoneNumber: string | undefined) => {
      try {
        await update({ phoneNumber });
        toast.success(t('toast.saved'));
      } catch (e) {
        toast.exception(e);
      }
    },
    [t, toast, update],
  );

  const submit = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        setTouched(false);
        setError(null);

        await verifyValidationCode({
          accountId: me?.id || '',
          code: codeRef.current,
        });

        onSuccess && onSuccess();
      } catch (e) {
        const errorMessage =
          e instanceof Error ? e.message : t('toast.genericError');
        setError(errorMessage);
      }
    },
    [verifyValidationCode, me, onSuccess, t],
  );

  if (notDelivered) {
    return (
      <div>
        <p className={classes.text}>
          {t('login.phoneVerificationValidationCode.notDeliveredText')}
        </p>
        <div className={classes.buttonGroup}>
          <Button type="button" onClick={onSuccess}>
            {t('login.phoneVerificationValidationCode.notDeliveredButton')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={submit} ref={formRef}>
        <div className={classes.illustrationContainer}>
          <img src={Phone} className={classes.illustration} alt="Phone" />
        </div>

        {isEditingPhone && (
          <Formik
            initialValues={{ phoneNumber: phone }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await updatePhone(values.phoneNumber);
                setIsEditingPhone(false);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ handleSubmit }) => (
              <div>
                <PhoneNumberInput
                  name="phoneNumber"
                  label={t('login.phoneVerificationValidationCode.phoneNumber')}
                />
                <div className={classes.updateButtonGroup}>
                  <Button
                    withBackArrow
                    type="button"
                    onClick={() => setIsEditingPhone(false)}
                    variant="transparent"
                  >
                    {t('login.phoneVerificationValidationCode.back')}
                  </Button>
                  <Button
                    isLoading={updating}
                    type="button"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmit();
                    }}
                  >
                    {t('login.phoneVerificationValidationCode.save')}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        )}

        {!isEditingPhone &&
          (!codeSent ? (
            <>
              <p className={classes.text}>
                {text
                  ? text
                  : t('login.phoneVerificationValidationCode.text', {
                      phone: phone
                        ? parsePhoneNumber(phone)?.formatInternational()
                        : '',
                    })}
              </p>
              <div className={classes.buttonGroup}>
                <Button
                  type="button"
                  onClick={() => setIsEditingPhone(true)}
                  className={classes.changePhoneLink}
                  variant="muted"
                >
                  {t('login.phoneVerificationValidationCode.changePhone')}
                </Button>
                <Button
                  type="button"
                  onClick={sendCode}
                  isLoading={sending || solvingCaptcha}
                  disabled={sending || solvingCaptcha || !phone}
                >
                  {t('login.phoneVerificationValidationCode.sendCode')}
                </Button>
              </div>
            </>
          ) : (
            <div className={classes.codeSection}>
              <p className={classes.text}>
                {t('login.phoneVerificationValidationCode.enterCode')}
              </p>
              <CodeInput
                ariaLabel="Code"
                length={6}
                onChange={onLetterChanged}
              />
              {!touched && error && <p className={classes.error}>{error}</p>}
              <div className={classes.resend}>
                <Trans
                  i18nKey="login.phoneVerificationValidationCode.resend"
                  components={{
                    a: (
                      <button
                        type="button"
                        onClick={resend}
                        className={classes.resendLink}
                        disabled={sending || solvingCaptcha}
                      />
                    ),
                  }}
                />
              </div>
            </div>
          ))}
      </form>
      {/* Outside of the form: an invisible widget would otherwise hook the code submission */}
      <Captcha ref={captchaRef} invisible />
    </>
  );
};
